import React, { useState, useMemo } from "react";
import { Box, Grid, Typography, Button, Tooltip as MuiTooltip, useTheme } from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";
import { LuZap, LuScale, LuShieldCheck, LuChevronRight, LuActivity, LuFlame, LuLayers } from "react-icons/lu";

const DEFAULT_COLORS = ["#16a34a", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

const formatValue = (kg) => {
    if (kg === undefined || kg === null) return "0";
    const abs = Math.abs(kg);
    if (abs >= 1000) return (kg / 1000).toFixed(1) + " t";
    return Math.round(kg).toLocaleString() + " kg";
};

/**
 * ShowcaseMetric - Displays real values and a relative progress bar
 */
const ShowcaseMetric = ({ label, value, displayValue, icon, color }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {icon}
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>{displayValue}</Typography>
            </Box>
            <Box sx={{ height: 6, bgcolor: isDark ? '#27272a' : '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.max(2, value)}%`, bgcolor: color, transition: 'width 1s cubic-bezier(0.16, 1, 0.3, 1)' }} />
            </Box>
        </Box>
    );
};

/**
 * CinematicShowcase - A premium, interactive Spotlight for Building LCA data.
 * Features animated Pie Charts, orbital rings, and normalized metric bars.
 * 
 * @param {Array} metrics - Processed building metrics
 * @param {Function} calculateVrttaScore - Optional function to calculate the efficiency score
 * @param {Array} colors - Theme colors for fallback
 */
const CinematicShowcase = ({ metrics, calculateVrttaScore, colors = DEFAULT_COLORS }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const [activeIndex, setActiveIndex] = useState(0);
    const m = metrics[activeIndex] || metrics[0];

    // 1. Calculate relative max values for normalization (Avoiding hardcoded assumptions)
    const maxMetrics = useMemo(() => {
        return {
            gwp: Math.max(...metrics.map(x => x.gwp || 0), 1),
            intensity: Math.max(...metrics.map(x => x.intensity || 0), 1),
            embodied: Math.max(...metrics.map(x => x.embodied || 0), 1)
        };
    }, [metrics]);

    // Default scoring logic if none provided
    const getScore = (current, baseline) => {
        if (calculateVrttaScore) return calculateVrttaScore(current, baseline);
        return 75; // Fallback
    };

    // Prepare real metrics for bars
    const gwpPct = (m.gwp / maxMetrics.gwp) * 100;
    const intensityPct = (m.intensity / maxMetrics.intensity) * 100;
    const embodiedPct = (m.embodied / maxMetrics.embodied) * 100;

    // Prepare LCA Stage Data for the internal Pie Chart
    const lcaData = useMemo(() => {
        if (!m.data?.lcaGroups) return [];
        return m.data.lcaGroups.map((g, idx) => ({
            name: g.stage_group,
            value: Math.abs(g.value)
        })).filter(v => v.value > 0);
    }, [m]);

    return (
        <Box sx={{
            minHeight: 650,
            bgcolor: isDark ? '#09090b' : 'white',
            borderRadius: 6,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 6,
            pb: 14,
            border: `1px solid ${isDark ? '#27272a' : '#e2e8f0'}`,
            boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.03)'
        }}>
            {/* Background Texture/Glow */}
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: activeIndex % 2 === 0 ? '10%' : '80%',
                width: 500,
                height: 500,
                bgcolor: m.color,
                filter: 'blur(120px)',
                opacity: 0.08,
                transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)',
                zIndex: 0,
                transform: 'translate(-50%, -50%)'
            }} />

            <Grid container spacing={8} alignItems="center" sx={{ zIndex: 1 }}>
                {/* Visual Column */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ position: 'relative' }}>
                        {/* Orbital Rings - Light Version */}
                        <Box className="orbital-ring" sx={{
                            position: 'absolute', inset: -40, border: `1px dashed ${isDark ? 'rgba(255,255,255,0.05)' : '#e2e8f0'}`, borderRadius: '50%',
                            animation: 'rotate-orbital 30s linear infinite'
                        }} />
                        <Box sx={{
                            position: 'relative',
                            width: 320,
                            height: 320,
                            bgcolor: isDark ? '#18181b' : '#f8fafc',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${isDark ? '#27272a' : '#f1f5f9'}`,
                            boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.6)' : '0 20px 40px rgba(0,0,0,0.05)',
                            animation: 'float-visual 6s ease-in-out infinite'
                        }}>
                            <PieChart width={320} height={320}>
                                <Pie
                                    data={lcaData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
                                        const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
                                        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                                        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));
                                        if (percent < 0.05) return null;
                                        return (
                                            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '10px', fontWeight: '900', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                                                {`${name}\n${(percent * 100).toFixed(0)}%`}
                                            </text>
                                        );
                                    }}
                                    innerRadius={0}
                                    outerRadius={120}
                                    paddingAngle={2}
                                    dataKey="value"
                                    animationDuration={1500}
                                >
                                    {lcaData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
                                    ))}
                                </Pie>
                                <MuiTooltip title="Lifecycle Stages Distribution (Carbon)">
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '50%' }} />
                                </MuiTooltip>
                            </PieChart>

                            <Box sx={{
                                position: 'absolute',
                                bottom: -20,
                                bgcolor: isDark ? '#1c1c1e' : 'white',
                                px: 3, py: 1,
                                borderRadius: 10,
                                border: `1px solid ${isDark ? '#3f3f46' : '#e2e8f0'}`,
                                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.05)',
                                display: 'flex', alignItems: 'center', gap: 1
                            }}>
                                <Box sx={{ width: 8, height: 8, bgcolor: m.color, borderRadius: '50%', animation: 'pulse-glow 2s infinite' }} />
                                <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b', fontWeight: 900, tracking: 2 }}>ACTIVE SNAPSHOT</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Content Column */}
                <Grid item xs={12} md={6}>
                    <Box key={m.id} sx={{ animation: 'fade-slide-in 0.8s ease-out' }}>
                        <Typography sx={{ color: '#10b981', fontSize: 13, fontWeight: 800, mb: 1, letterSpacing: 4 }}>BUILDING SPECIFICATION</Typography>
                        <Typography sx={{ color: isDark ? '#f8fafc' : '#1e293b', fontSize: 32, fontWeight: 900, lineHeight: 1.1, mb: 2 }}>{m.name}</Typography>
                        <Typography sx={{ color: isDark ? '#a1a1aa' : '#64748b', fontSize: 16, mb: 4, maxWidth: 450 }}>
                            A high-fidelity carbon audit of the {m.name} lifecycle. Benchmarking {m.area?.toLocaleString() || "N/A"} m² of data with a VRTTA Index of {getScore(m, metrics[0])}.
                        </Typography>

                        <Box sx={{ bgcolor: isDark ? '#1c1c1e' : '#f8fafc', p: 4, borderRadius: 4, border: `1px solid ${isDark ? '#27272a' : '#f1f5f9'}` }}>
                            <ShowcaseMetric
                                label="Total Carbon Content"
                                value={gwpPct}
                                displayValue={formatValue(m.gwp)}
                                icon={<LuFlame size={16} />}
                                color={m.color}
                            />
                            <ShowcaseMetric
                                label="Environmental Intensity"
                                value={intensityPct}
                                displayValue={`${Number(m.intensity).toFixed(1)} kg/m²`}
                                icon={<LuZap size={16} />}
                                color={m.color}
                            />
                            <ShowcaseMetric
                                label="Structural Carbon Value"
                                value={embodiedPct}
                                displayValue={formatValue(m.embodied)}
                                icon={<LuLayers size={16} />}
                                color={m.color}
                            />

                            <Button variant="text" sx={{ mt: 1, color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 700, fontSize: 12, '&:hover': { bgcolor: 'transparent', color: m.color } }} endIcon={<LuChevronRight size={14} />}>
                                VIEW DETAILED LCA RECAP
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Switcher Island - Light Version */}
            <Box sx={{
                position: 'absolute',
                bottom: 30,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: isDark ? 'rgba(24, 24, 27, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                p: 0.8,
                borderRadius: 10,
                border: `1px solid ${isDark ? '#3f3f46' : '#e2e8f0'}`,
                display: 'flex', gap: 1,
                boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.08)'
            }}>
                {metrics.map((item, idx) => (
                    <Button
                        key={item.id}
                        onClick={() => setActiveIndex(idx)}
                        sx={{
                            borderRadius: 10,
                            px: 3,
                            py: 1,
                            minWidth: 120,
                            color: activeIndex === idx ? (isDark ? '#f8fafc' : '#1e293b') : (isDark ? '#71717a' : '#94a3b8'),
                            bgcolor: activeIndex === idx ? (isDark ? '#3f3f46' : '#f1f5f9') : 'transparent',
                            '&:hover': { bgcolor: isDark ? '#27272a' : '#f8fafc' },
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                        }}
                    >
                        {activeIndex === idx && <Box sx={{ position: 'absolute', bottom: -1, width: 24, height: 2, bgcolor: metrics[idx].color, borderRadius: 2 }} />}
                        {item.name}
                    </Button>
                ))}
            </Box>

            <style>{`
                @keyframes rotate-orbital { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes float-visual { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
                @keyframes pulse-glow { 0% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.4); } 100% { opacity: 1; transform: scale(1); } }
                @keyframes fade-slide-in { from { opacity: 0; transform: translateX(30px); filter: blur(8px); } to { opacity: 1; transform: translateX(0); filter: blur(0px); } }
            `}</style>
        </Box>
    );
};

export default CinematicShowcase;
